"use client"

/**
 * Leo's category suggestions. `useCategorySuggestionCards` reads them as cards
 * with their Add / Skip handlers, so Properties can page them in the one Leo
 * spotted stack; `LeoCategorySuggestions` shows them as a card of their own
 * (the category navigation). One card per suggestion; Add applies it, Skip
 * turns it down. Nothing is applied until Add.
 */

import * as React from "react"

import { useQuestionComposerContext } from "@/components/question-composer/composer-context"
import { LeoSpottedQueueCard } from "@/components/question-quality/leo-spotted-queue-card"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { useIsProgramDirector } from "@/contexts/exam-role-context"
import { useCategoryDimensions } from "@/hooks/use-category-dimensions"
import { useCategoryCrosswalk } from "@/hooks/use-category-crosswalk"
import { useLeoCategoryPicks } from "@/hooks/use-leo-category-picks"
import { mergeCategoryPicks } from "@/lib/exam-categories/merge-picks"
import { recordSuggestionFeedback, useSuggestionFeedback } from "@/lib/exam-categories/suggestion-feedback"
import {
  buildDimensionLookup,
  missingAncestorsByDimension,
  getCategoryBreadcrumb,
} from "@/lib/exam-categories"
import { leadInPlainText } from "@/lib/lead-in-html"
import type { LeoSpottedCard } from "@/lib/question-quality/leo-spotted-queue"

export type CategorySuggestionsInput = {
  /** The tree `scopeIds` limits (course content). Absent: every tree in `dimensionIds`. */
  scopeDimensionId?: string
  /** Leo's other classification findings (a required standard with no tag), first among the cards. */
  extraCards?: readonly LeoSpottedCard[]
  onExtraPrimary?: (card: LeoSpottedCard) => void
  /** Which trees this covers. */
  dimensionIds: readonly string[]
  /** Every tag on the question, so an added one stops being offered. */
  value: readonly string[]
  onAdd: (categoryId: string) => void
  /** Add several at once, as a person's own choice (the broader categories). */
  onAddMany: (categoryIds: string[]) => void
  minConfidence?: number
  /** For course content: only what this course teaches. */
  scopeIds?: readonly string[]
}

/** Leo's classification cards, and what Add and Skip do on each. Handlers return false for a card that is not theirs. */
export function useCategorySuggestionCards({
  dimensionIds,
  value,
  onAdd,
  onAddMany,
  minConfidence = 0,
  scopeIds,
  scopeDimensionId,
  extraCards = [],
  onExtraPrimary,
}: CategorySuggestionsInput) {
  const { dismissedCategoryKeys, dismissCategorySuggestion, form } = useQuestionComposerContext()
  const { categories } = useExamCategories()
  const { dimensions } = useCategoryDimensions()
  const isDirector = useIsProgramDirector()
  const [extraSkipped, setExtraSkipped] = React.useState<ReadonlySet<string>>(new Set())
  const stem = leadInPlainText(form.watch("leadIn") ?? "")
  // What this tab offers: for course content only what the course teaches.
  const offered = React.useMemo(() => {
    const scope = scopeIds && scopeIds.length > 0 ? new Set(scopeIds) : null
    const lookup = buildDimensionLookup(categories)
    return scope
      ? categories.filter((category) => {
          const dimension = lookup.get(category.id) ?? ""
          const scoped = scopeDimensionId ? dimension === scopeDimensionId : dimensionIds.includes(dimension)
          return !scoped || scope.has(category.id)
        })
      : categories
  }, [categories, dimensionIds, scopeDimensionId, scopeIds])
  const catalog = React.useMemo(() => {
    const lookup = buildDimensionLookup(offered)
    return offered.filter((category) => dimensionIds.includes(lookup.get(category.id) ?? ""))
  }, [dimensionIds, offered])
  const { picks: modelPicks, status: modelStatus } = useLeoCategoryPicks(stem, catalog)
  const feedback = useSuggestionFeedback()
  const { links } = useCategoryCrosswalk()

  const rows = React.useMemo(
    () =>
      mergeCategoryPicks({
        stem,
        offered,
        dimensions: dimensions.filter((dimension) => dimensionIds.includes(dimension.id)),
        value,
        dismissedIds: dismissedCategoryKeys,
        isDirector,
        modelPicks,
        modelStatus,
        links,
        feedback,
        minConfidence,
      }),
    [dimensionIds, dimensions, dismissedCategoryKeys, feedback, isDirector, links, minConfidence, modelPicks, modelStatus, offered, stem, value],
  )

  // A deep choice with its parents missing: a blueprint line built on a parent
  // would never see this question. Part of the same card, offered, never added.
  const [broaderDismissed, setBroaderDismissed] = React.useState(false)
  const broader = React.useMemo(() => {
    const scope = scopeIds && scopeIds.length > 0 ? new Set(scopeIds) : null
    return missingAncestorsByDimension(categories, [...value])
      .filter((row) => dimensionIds.includes(row.dimensionId))
      .flatMap((row) => row.categoryIds)
      .filter((id) => !scope || scope.has(id))
  }, [categories, dimensionIds, scopeIds, value])
  const broaderCard: LeoSpottedCard | null =
    broader.length > 0 && !broaderDismissed
      ? {
          id: "broader",
          kind: "category",
          dimension: "category",
          badge: "Category",
          header: "Add the broader categories too?",
          body: `${broader
            .map((id) => categories.find((category) => category.id === id)?.name)
            .filter(Boolean)
            .join(", ")}. Blueprints and reports on a broader category only see this question when it is tagged there too.`,
          primaryLabel: `Add ${broader.length}`,
          issues: [],
          state: "open" as const,
        }
      : null

  const suggestionCards: LeoSpottedCard[] = rows.map((row) => {
          const trail = getCategoryBreadcrumb(categories, row.categoryId)
          return {
            id: `suggest:${row.categoryId}`,
            kind: "category",
            dimension: "category",
            badge: "Category",
            header: trail[trail.length - 1]?.name ?? "",
            confidence: row.confidence,
            confidenceWhy: row.why,
            path: trail.slice(0, -1).map((node) => node.name),
            body: row.reason,
            primaryLabel: "Add category",
            issues: [],
            state: "open" as const,
          }
        })
  const extras = extraCards.map((card) => ({ ...card, state: extraSkipped.has(card.id) ? ("skipped" as const) : card.state }))
  const cards = [...extras, ...(broaderCard ? [broaderCard] : []), ...suggestionCards]
  const isExtra = (card: LeoSpottedCard) => extraCards.some((extra) => extra.id === card.id)
  const owns = (card: LeoSpottedCard) => isExtra(card) || card.id === "broader" || card.id.startsWith("suggest:")

  const primary = (card: LeoSpottedCard): boolean => {
    if (!owns(card)) return false
    if (isExtra(card)) onExtraPrimary?.(card)
    else if (card.id === "broader") onAddMany(broader)
    else {
      const id = card.id.slice("suggest:".length)
      recordSuggestionFeedback(id, "added")
      onAdd(id)
    }
    return true
  }
  const skip = (card: LeoSpottedCard): boolean => {
    if (!owns(card)) return false
    if (isExtra(card)) setExtraSkipped((current) => new Set([...current, card.id]))
    else if (card.id === "broader") setBroaderDismissed(true)
    else {
      const id = card.id.slice("suggest:".length)
      recordSuggestionFeedback(id, "skipped")
      dismissCategorySuggestion(id)
    }
    return true
  }
  const reopen = (card: LeoSpottedCard): boolean => {
    if (!isExtra(card)) return owns(card)
    setExtraSkipped((current) => {
      const next = new Set(current)
      next.delete(card.id)
      return next
    })
    return true
  }
  return { cards, primary, skip, reopen }
}

/** Leo's classification cards as a card of their own. Nothing to offer: `fallback` (or nothing). */
export function LeoCategorySuggestions({ fallback = null, ...input }: CategorySuggestionsInput & { fallback?: React.ReactNode }) {
  const { cards, primary, skip, reopen } = useCategorySuggestionCards(input)
  const [index, setIndex] = React.useState(0)
  if (cards.length === 0) return <>{fallback}</>
  return (
    <LeoSpottedQueueCard
      heading="Leo suggests"
      tag={null}
      queue={{ cards, activeIndex: Math.min(index, cards.length - 1) }}
      onNavigate={setIndex}
      onPrimary={primary}
      onSkip={skip}
      onReopen={reopen}
    />
  )
}
